import { ViewType } from 'structure/pv2/dynavault/components/ViewTypeSwitch';
import { VaultListTable } from 'structure/pv2/dynavault/components/VaultListTable';
import { VaultListCards } from './VaultListCards';
import { useClientRenderDelay } from 'utils/hooks/useClientRenderDelay';
import { useVaultListWithFallback } from 'structure/pv2/dynavault/hooks/useVaultList';
import { useConnection } from 'wagmi';
import { Loader } from '@dapp/multi-lib/ui/loader';
import {
  useVaultListOnChainData,
  VaultOnChainData,
} from 'structure/pv2/dynavault/hooks/useVaultListBlockchain';
import {
  Sort,
  useVaultListContext,
} from 'structure/pv2/dynavault/context/VaultListContext';
import { DynaVault } from '../../../../api/DynaVaultsAPI';
import { getVaultAPY } from 'structure/pv2/vault/utils/vault';
import { useMemo } from 'react';
import { matchesVaultStatusFilter } from 'structure/pv2/dynavault/utils/vaultStatus';

const sortVaultList = (
  vaultList: DynaVault[],
  onChainData: Record<`0x${string}`, VaultOnChainData>,
  sort: Sort
): DynaVault[] => {
  if (!sort || sort.direction === 'default' || !onChainData) {
    return vaultList;
  }

  return [...vaultList].sort((a, b) => {
    let aValue: number | string = 0;
    let bValue: number | string = 0;

    switch (sort.name) {
      case 'tvl':
        aValue = Number(onChainData[a.address]?.tvlUsd || 0);
        bValue = Number(onChainData[b.address]?.tvlUsd || 0);
        break;

      case 'position':
        aValue = onChainData[a.address]?.positionInUsd || 0;
        bValue = onChainData[b.address]?.positionInUsd || 0;
        break;
      case 'apy':
        aValue = getVaultAPY(a);
        bValue = getVaultAPY(b);
        break;
      case 'price':
        aValue = Number(onChainData[a.address]?.vaultPrice) || 0;
        bValue = Number(onChainData[b.address]?.vaultPrice) || 0;
        break;
      default:
        break;
    }

    if (sort.direction === 'desc') {
      return aValue > bValue ? -1 : 1;
    }

    return aValue > bValue ? 1 : -1;
  });
};

export const VaultList = () => {
  const { viewType, vaultStatusFilter, sort } = useVaultListContext();

  const csr = useClientRenderDelay();
  const vaultListQuery = useVaultListWithFallback();
  const account = useConnection();

  const filteredVaultList = useMemo(() => {
    return (vaultListQuery.data || []).filter((vault) =>
      matchesVaultStatusFilter(vault, vaultStatusFilter)
    );
  }, [vaultListQuery.data, vaultStatusFilter]);

  const onChainDataQuery = useVaultListOnChainData({
    vaultAddressList: filteredVaultList.map((vault) => vault.address),
    walletAddress: account.address as `0x${string}`,
    chainId: 8453,
    query: {
      staleTime: 15000,
      refetchInterval: 15000,
      enabled: !!filteredVaultList.length,
    },
  });

  const sortedVaultList = useMemo(() => {
    return sortVaultList(filteredVaultList, onChainDataQuery.data ?? {}, sort);
  }, [filteredVaultList, onChainDataQuery.data, sort]);

  if (!csr) {
    return <Loader color='white' />;
  }

  if (vaultListQuery.isLoading) {
    return <Loader color='white' />;
  }

  return (
    <>
      {viewType === ViewType.TABLE ? (
        <VaultListTable vaultList={sortedVaultList} />
      ) : null}
      {viewType === ViewType.CARD ? (
        <VaultListCards
          vaultList={sortedVaultList}
          vaultStatusFilter={vaultStatusFilter}
        />
      ) : null}
    </>
  );
};
